import { useState, useCallback, memo } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { Download, Copy, Trash2, Wand2, FilmIcon, Flag, Video } from "lucide-react";
import { Toast, ConfirmDialog, UpgradePlansModal } from "@/components/ui";
import { ReportContentModal } from "@/components/features/report/report-content-modal";
import { formatDate, cn } from "@/lib/utils";
import { useUserStore, getDisplayUrl } from "@/stores/user-store";
import { useDownload } from "@/hooks";
import { hasAccessToPlan } from "@/config/navigation";
import { AssetPreview } from "./asset-preview";
import { CreatorFeaturesMenu } from "./creator-features-menu";
import { ActionButton } from "./action-button";
import { AnalyticsService } from "@/services/analytics";
import type { Asset } from "@/types";

interface AssetCardProps {
  asset: Asset;
  onPlay: () => void;
  onToggleFavorite: () => void;
  onCopyPrompt?: () => void;
  onDelete?: () => void;
  /** If true, card will animate in as new */
  isNew?: boolean;
}

/**
 * Memoized asset card to prevent re-renders when parent list updates.
 * Only re-renders when asset data or callbacks change.
 */
export const AssetCard = memo(function AssetCard({
  asset,
  onPlay,
  onToggleFavorite,
  onCopyPrompt,
  onDelete,
  isNew = false,
}: AssetCardProps) {
  const navigate = useNavigate();
  const { isSignedIn } = useUser();
  const { plan: userPlan } = useUserStore();
  const hasCreatorAccess = !!(
    isSignedIn && hasAccessToPlan(userPlan, "creator")
  );

  // Derive display properties from asset (must be before callbacks that use them)
  const assetType = asset.media_type === "VIDEO" ? "video" : "image";
  const isUpload = asset.source === "upload";
  // Display title: show prompt for generated assets, nothing for uploads
  const displayTitle = isUpload
    ? null
    : asset.prompt?.slice(0, 50) || `${assetType} ${asset.id.slice(0, 8)}`;
  // Internal title for filenames and dialogs - always needs a value
  const assetTitle =
    asset.prompt?.slice(0, 50) || `${assetType}_${asset.id.slice(0, 8)}`;
  const showCreatorMenu =
    asset.media_type === "VIDEO" && asset.source === "generation";
  const isProcessing =
    asset.status === "processing" || asset.status === "pending";
  
  // Check if processing for > 10 minutes
  const processingTime = asset.created_at 
    ? (Date.now() - new Date(asset.created_at).getTime()) / 1000 / 60
    : 0;
  const isLongRunning = isProcessing && processingTime > 10;

  // UI state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState("");
  const [showReportModal, setShowReportModal] = useState(false);

  // Download hook
  const {
    download,
    showToast: showDownloadToast,
    setShowToast: setShowDownloadToast,
    progress: downloadProgress,
    isComplete: downloadComplete,
    isError: downloadError,
    assetType: downloadAssetType,
  } = useDownload();

  // Action handlers
  const handleExtendVideo = useCallback(() => {
    navigate("/extend-video", { state: { video: asset } });
  }, [navigate, asset]);

  const handleAnimateImage = useCallback(() => {
    // Forward upload_id / generation id so the backend can resolve a
    // fresh signed URL instead of trusting the stale `asset.url`.
    navigate("/video/i2v", {
      state: {
        imageUrl: asset.url,
        ...(asset.source === "upload"
          ? { uploadId: asset.id }
          : { imageId: asset.id }),
      },
    });
  }, [navigate, asset.url, asset.id, asset.source]);

  const handleEditImage = useCallback(() => {
    navigate("/images/image-edit", {
      state: {
        imageUrl: asset.url,
        ...(asset.source === "upload"
          ? { uploadId: asset.id }
          : { imageId: asset.id }),
      },
    });
  }, [navigate, asset.url, asset.id, asset.source]);

  const handleCopyPrompt = useCallback(() => {
    onCopyPrompt?.();
    setShowCopyToast(true);
    // Track prompt copy event
    AnalyticsService.trackPromptCopied(asset.id, assetType);
  }, [onCopyPrompt, asset.id, assetType]);

  const handleToggleFavorite = useCallback(() => {
    // Track favorite event (toggle)
    AnalyticsService.trackAssetFavorited(asset.id, assetType, undefined, !asset.is_favorite);
    onToggleFavorite();
  }, [onToggleFavorite, asset.id, assetType, asset.is_favorite]);

  const handleDownload = useCallback(
    (useCleanUrl = false) => {
      // Determine which URL to download (watermarked by default, clean for creators)
      const downloadUrl =
        useCleanUrl && asset.url_watermarked
          ? asset.url
          : asset.url_watermarked || asset.url;
      const extension = assetType === "video" ? "mp4" : "png";
      const filename = `${assetTitle.slice(0, 30).replace(/[^a-z0-9]/gi, "_")}_${asset.id.slice(0, 8)}.${extension}`;

      // Track download event
      AnalyticsService.trackAssetDownloaded(asset.id, assetType, undefined, !useCleanUrl && !!asset.url_watermarked);

      download({ url: downloadUrl, filename, assetType });
    },
    [
      asset.url,
      asset.url_watermarked,
      asset.id,
      assetType,
      assetTitle,
      download,
    ],
  );

  const handleConfirmDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setIsDeleting(true);
    // Wait for animation to complete before calling delete
    setTimeout(() => {
      onDelete?.();
    }, 250); // Match animation duration
  }, [onDelete]);

  // Creator feature handlers
  const handleDownloadNoWatermark = useCallback(() => {
    if (!hasCreatorAccess) {
      setUpgradeFeature("Download without Watermark");
      setShowUpgradeModal(true);
      setShowMoreMenu(false);
      return;
    }
    setShowMoreMenu(false);
    // Download clean URL (without watermark)
    handleDownload(true);
  }, [hasCreatorAccess, handleDownload]);

  // const handleDispute = useCallback(() => {
  //   if (!hasCreatorAccess) {
  //     setUpgradeFeature('Dispute Generation')
  //     setShowUpgradeModal(true)
  //     setShowMoreMenu(false)
  //     return
  //   }
  //   console.log('Disputing generation:', asset.id)
  //   setShowMoreMenu(false)
  // }, [hasCreatorAccess, asset.id])

  const handleUpgrade = useCallback(() => {
    setUpgradeFeature("Creator Features");
    setShowUpgradeModal(true);
    setShowMoreMenu(false);
  }, []);

  return (
    <>
      {/* Modals & Toasts */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        title={`Delete "${assetTitle}"?`}
        confirmLabel="Delete"
        variant="danger"
      />
      <Toast
        isOpen={showCopyToast}
        onClose={() => setShowCopyToast(false)}
        type="success"
        message="Prompt copied to clipboard"
      />
      <Toast
        isOpen={showDownloadToast}
        onClose={() => setShowDownloadToast(false)}
        type={downloadError ? "error" : "loading"}
        message={
          downloadError
            ? "Download failed. Please try again later."
            : downloadComplete
              ? "Download complete!"
              : `Downloading ${downloadAssetType}...`
        }
        progress={downloadError ? undefined : downloadProgress}
        isComplete={downloadComplete}
        duration={downloadError ? 4000 : 2000}
      />
      <UpgradePlansModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        preset="upgrade"
        presetContext={{ feature: upgradeFeature }}
        allowedPlans={["creator", "pro"]}
      />
      <ReportContentModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        generationId={asset.id}
      />

      {/* Card */}
      <div
        className={cn(
          "glass rounded-xl overflow-hidden group relative",
          isNew && "animate-card-in",
          isDeleting && "animate-card-out",
        )}
      >
        {/* Long-running generation notice */}
        {isLongRunning && (
          <div className="absolute top-2 left-2 right-2 px-2 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 backdrop-blur-sm z-10">
            <p className="text-xs text-amber-300/90 text-center leading-tight">
              Credits will be refunded for failed generations within 24 hours
            </p>
          </div>
        )}

        <AssetPreview
          type={assetType}
          thumbnail={getDisplayUrl(asset, userPlan)}
          title={displayTitle}
          isFavorite={asset.is_favorite}
          isProcessing={isProcessing}
          onPlay={onPlay}
          onToggleFavorite={handleToggleFavorite}
        />

        {/* Info & Actions */}
        <div className="px-3 py-2 flex items-center justify-between gap-3">
          <div className="min-w-0 max-w-[50%]">
            {displayTitle && (
              <h3 className="text-sm font-medium text-white truncate">
                {displayTitle}
              </h3>
            )}
            <p className="text-xs text-[#555] mt-0.5">
              {formatDate(asset.created_at)}
            </p>
          </div>
          <div className="flex gap-0 shrink-0">
            {isProcessing ? (
              // Show minimal actions for processing assets
              <span className="text-xs text-violet-400 px-2 py-1">
                Processing...
              </span>
            ) : (
              <>
                {assetType === "video" ? (
                  <ActionButton
                    icon={FilmIcon}
                    tooltip="Extend Video"
                    onClick={handleExtendVideo}
                  />
                ) : (
                  <>
                    {/* Image funnel: edit then animate. Wand2 = edit
                        (matches Image Edit page); Video = animate
                        (matches I2V page). */}
                    <ActionButton
                      icon={Wand2}
                      tooltip="Edit Image"
                      onClick={handleEditImage}
                    />
                    <ActionButton
                      icon={Video}
                      tooltip="Animate Image"
                      onClick={handleAnimateImage}
                    />
                  </>
                )}
                <ActionButton
                  icon={Download}
                  tooltip="Download"
                  onClick={() => handleDownload(false)}
                />
                {asset.prompt &&
                  asset.source === "generation" &&
                  onCopyPrompt && (
                    <ActionButton
                      icon={Copy}
                      tooltip="Copy Prompt"
                      onClick={handleCopyPrompt}
                    />
                  )}
                {showCreatorMenu && (
                  <CreatorFeaturesMenu
                    isOpen={showMoreMenu}
                    onToggle={() => setShowMoreMenu(!showMoreMenu)}
                    onClose={() => setShowMoreMenu(false)}
                    hasCreatorAccess={hasCreatorAccess}
                    onDownloadNoWatermark={handleDownloadNoWatermark}
                    // onDispute={handleDispute}
                    onUpgrade={handleUpgrade}
                  />
                )}
                {asset.source === "generation" && (
                  <ActionButton
                    icon={Flag}
                    tooltip="Report"
                    onClick={() => setShowReportModal(true)}
                  />
                )}
                <ActionButton
                  icon={Trash2}
                  tooltip="Delete"
                  variant="danger"
                  onClick={() => setShowDeleteConfirm(true)}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
});
